import React, { useState } from 'react';
import { Download, Upload, ArrowRight, Info, X } from 'lucide-react';
import api from '../services/api';


function Backups() {
  const [user] = useState(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [portStrategy, setPortStrategy] = useState('auto-assign');
  const [portWarningOpen, setPortWarningOpen] = useState(false);
  const [portConflicts, setPortConflicts] = useState([]);

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      await api.downloadBackup();
      alert('Backup downloaded successfully');
    } catch (err) {
      alert('Failed to download backup: ' + err.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreBackupSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBackupLoading(true);
    try {
      const infoResult = await api.getBackupInfo(file);
      if (infoResult.success) {
        setBackupInfo({ file, info: infoResult.info });
        setRestoreModalOpen(true);
      } else {
        alert('Invalid backup file: ' + infoResult.error);
      }
    } catch (err) {
      alert('Failed to read backup: ' + err.message);
    } finally {
      setBackupLoading(false);
      event.target.value = '';
    }
  };

  const handleConfirmRestore = async () => {
    if (!backupInfo) return;

    setBackupLoading(true);
    try {
      const result = await api.uploadBackup(backupInfo.file, true, portStrategy);
      if (result.success) {
        // Check if there were port changes
        if (result.portChanges && result.portChanges.length > 0) {
          setPortConflicts(result.portChanges);
          setPortWarningOpen(true);
        } else {
          alert(result.message);
        }
        setRestoreModalOpen(false);
        setBackupInfo(null);
      } else {
        alert(result.error || 'Restore failed');
      }
    } catch (err) {
      alert('Failed to restore backup: ' + err.message);
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <div className="vercel-deploy-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      
      <div className="under-dev-container">
        {/* Decorative background glow */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '200px',
          height: '200px',
          background: 'var(--primary)',
          opacity: 0.15,
          filter: 'blur(60px)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <div style={{ 
          width: '64px', 
          height: '64px', 
          background: 'linear-gradient(135deg, var(--primary), var(--primary-hover))', 
          borderRadius: '20px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          margin: '0 auto 2rem',
          boxShadow: '0 10px 20px -5px rgba(var(--primary-rgb), 0.4)'
        }}>
          <Info size={32} color="white" strokeWidth={2.5} />
        </div>
        
        <h2 className="under-dev-title">
          Under Development
        </h2>
        
        <p className="under-dev-description">
          This module is currently undergoing extensive testing and upgrades to ensure maximum reliability for your disaster recovery workflows.
        </p>

        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1.25rem',
          background: 'rgba(var(--primary-rgb), 0.1)',
          borderRadius: '999px',
          border: '1px solid rgba(var(--primary-rgb), 0.2)'
        }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)', animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>Available Soon</span>
        </div>
      </div>

      <div style={{ display: 'none' }}>
      <div className="form-section-group" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className="form-section-header">
          <div className="form-section-title-group">
            <h2 className="form-section-title">Disaster Recovery</h2>
            <p className="form-section-subtitle">Maintain the integrity of your infrastructure with comprehensive backups and restoration.</p>
          </div>
        </div>

        {/* Download Backup */}
        <div className="vercel-premium-form" style={{ marginBottom: '2rem' }}>
          <div className="form-section-header">
            <div className="form-section-title-group">
              <h3 style={{ fontSize: '1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Download size={18} /> Download Infrastructure Snapshot
              </h3>
            </div>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Export all deployments, container images, and configurations as a single portable archive.
            </p>
            <div style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--surface-2)', borderRadius: '12px', border: '1px solid var(--border)' }}>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {['Metadata & Configs', 'Source & Images', 'Compose Files', 'Network Settings'].map(item => (
                  <li key={item} style={{ fontSize: '0.8rem', color: 'var(--text-main)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }}></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <button
              className="action-btn-premium primary"
              onClick={handleDownloadBackup}
              disabled={backupLoading}
              style={{ height: '48px', width: '100%', justifyContent: 'center' }}
            >
              <Download size={18} />
              <span>{backupLoading ? 'Generating Snapshot...' : 'Create & Download Backup'}</span>
            </button>
          </div>
        </div>

        {/* Restore Backup */}
        <div className="vercel-premium-form" style={{ marginBottom: '2rem' }}>
          <div className="form-section-header">
            <div className="form-section-title-group">
              <h3 style={{ fontSize: '1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Upload size={18} /> Restore Environment
              </h3>
            </div>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Select a previously created ZIP archive to restore your environment. Port conflicts will be resolved based on your preference.
            </p>
            
            <label className="action-btn-premium" style={{ height: '48px', width: '100%', justifyContent: 'center', cursor: backupLoading ? 'not-allowed' : 'pointer', opacity: backupLoading ? 0.6 : 1 }}>
              <Upload size={18} />
              <span>{backupLoading ? 'Reading Archive...' : 'Select Backup Archive'}</span>
              <input
                type="file"
                accept=".zip"
                onChange={handleRestoreBackupSelect}
                disabled={backupLoading}
                style={{ display: 'none' }}
              />
            </label>
            
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(var(--primary-rgb), 0.05)', borderRadius: '12px', border: '1px solid rgba(var(--primary-rgb), 0.1)', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <Info size={16} style={{ color: 'var(--primary)', marginTop: '0.1rem' }} />
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Restoring from another user's backup will automatically reassign ownership to you.
              </p>
            </div>
          </div>
        </div>

        {/* Features Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {[
            { title: 'Full Environment', desc: 'Backups include code, images, and every config detail.' },
            { title: 'Transferable', desc: 'Snapshots can be restored to any account or instance.' }
          ].map(f => (
            <div key={f.title} style={{ padding: '1.25rem', borderRadius: '16px', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--text-main)', marginBottom: '0.25rem' }}>{f.title}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Restore Confirmation Modal */}
      {restoreModalOpen && backupInfo && (
        <div className="dashboard-modal-overlay active">
          <div className="dashboard-modal-content active" style={{ maxWidth: '550px' }}>
            <div className="dashboard-modal-header" style={{ padding: '1.5rem 2rem' }}>
              <h2 className="dashboard-modal-title">Confirm Restoration</h2>
              <button className="dashboard-modal-close" onClick={() => { setRestoreModalOpen(false); setBackupInfo(null); }}><X size={20} /></button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1.5rem', padding: '1.25rem', background: 'var(--surface-2)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: '0.75rem', letterSpacing: '0.05em' }}>Backup Identity</div>
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.88rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Originator</span>
                    <span style={{ fontWeight: 700 }}>{backupInfo.info.username}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Creation Date</span>
                    <span style={{ fontWeight: 700 }}>{backupInfo.info.backupDate}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Deployments</span>
                    <span style={{ fontWeight: 700 }}>{Object.keys(backupInfo.info.apps || {}).length}</span>
                  </div>
                </div>
              </div>

              {backupInfo.info.username !== user?.username && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(var(--warning-rgb), 0.1)', borderRadius: '10px', border: '1px solid var(--warning)', color: 'var(--warning)', fontSize: '0.85rem', fontWeight: 700 }}>
                   ⚠️ You are about to take ownership of {Object.keys(backupInfo.info.apps || {}).length} applications.
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '2rem' }}>
                <label style={{ marginBottom: '1rem' }}>Port Conflict Resolution</label>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {[
                    { id: 'auto-assign', label: 'Auto-assign', desc: 'Generate new ports for conflicts' },
                    { id: 'warn', label: 'Warn Only', desc: 'Restore and notify about conflicts' },
                    { id: 'reject', label: 'Strict', desc: 'Abort if any port is taken' }
                  ].map(opt => (
                    <label key={opt.id} className={`action-btn-premium ${portStrategy === opt.id ? 'active primary' : ''}`} style={{ height: 'auto', padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.1rem', cursor: 'pointer' }}>
                      <input type="radio" value={opt.id} checked={portStrategy === opt.id} onChange={(e) => setPortStrategy(e.target.value)} style={{ display: 'none' }} />
                      <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{opt.label}</div>
                      <div style={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 500 }}>{opt.desc}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="action-btn-premium" onClick={() => { setRestoreModalOpen(false); setBackupInfo(null); }} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button className="action-btn-premium primary" onClick={handleConfirmRestore} style={{ flex: 2, justifyContent: 'center' }}>
                  {backupLoading ? 'Restoring...' : 'Start Restoration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Port Conflict Notice */}
      {portWarningOpen && portConflicts.length > 0 && (
        <div className="dashboard-modal-overlay active" style={{ zIndex: 1100 }}>
          <div className="dashboard-modal-content active" style={{ maxWidth: '600px' }}>
            <div className="dashboard-modal-header" style={{ padding: '1.5rem 2rem' }}>
              <h2 className="dashboard-modal-title" style={{ color: 'var(--warning)' }}>⚠️ Port Reassignment</h2>
            </div>
            
            <div style={{ padding: '2rem' }}>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                Conflicts detected. The following applications were assigned new ports to avoid collisions:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                {portConflicts.map((conflict, idx) => (
                  <div key={idx} style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: '12px', border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{conflict.appName}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>{conflict.originalPort}</span>
                      <ArrowRight size={14} style={{ opacity: 0.3 }} />
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--success)' }}>{conflict.newPort}</span>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="action-btn-premium primary"
                onClick={() => { setPortWarningOpen(false); setPortConflicts([]); }}
                style={{ width: '100%', height: '48px', justifyContent: 'center' }}
              >
                Acknowledge Changes
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Backups;
